import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    useCallback,
    useContext,
    useState,
    Children,
    useMemo,
    type FC,
    useId
} from "react";
import {
    View
} from "react-native";
import type ISidebarMenuItemProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSidebarRootContext,
    SidebarItemContext,
    SidebarSubContext
} from "../../context";
import type {
    SidebarItemContextType
} from "../../type";
import type ISidebarMenuBadgeProps from "../menuBadge/type";
import type ISidebarMenuButtonProps from "../menuButton/type";
import type ISidebarMenuSubProps from "../menuSub/type";
import type ISidebarMenuSubButtonProps from "../menuSubButton/type";
import {
    getSidebarActionPlacement,
    getSidebarRowMeasures,
    getSidebarMeasures
} from "../../stylesheet";
import {
    getSidebarOverlaySide,
    getSidebarBadgeText,
    pressSidebarButton
} from "../../util";
import SidebarMenuSubButton from "../menuSubButton";
import DropdownMenu from "../../../dropdownMenu";
import SidebarMenuSubItem from "../menuSubItem";
import SidebarMenuButton from "../menuButton";
import SidebarMenuBadge from "../menuBadge";
import SidebarMenuSub from "../menuSub";

const isPartElement = <T, >(item: ReactNode, part: FC<T>): item is ReactElement<T> => {
    return isValidElement(item) && item.type === part;
};

const getPartElement = <T, >(items: Array<ReactNode>, part: FC<T>): ReactElement<T> | undefined => {
    const element = items.find((item) => isPartElement<T>(item, part));

    return isValidElement<T>(element) ? element : undefined;
};

const getSubEntries = (children: ReactNode): Array<ReactNode> => {
    return Children.toArray(children).flatMap((child) => {
        if(isValidElement<{
            children?: ReactNode;
        }>(child) && child.type === SidebarMenuSubItem) {
            return Children.toArray(child.props.children);
        }

        return [
            child
        ];
    });
};

const getHasActiveSub = (children: ReactNode): boolean => {
    return getSubEntries(children).some((entry) => {
        if(isPartElement<ISidebarMenuSubButtonProps>(entry, SidebarMenuSubButton)) {
            return !!entry.props.isActive;
        }

        if(!isPartElement<ISidebarMenuItemProps>(entry, SidebarMenuItem)) {
            return false;
        }

        const nestedItems = Children.toArray(entry.props.children);

        const nestedButton = getPartElement<ISidebarMenuButtonProps>(nestedItems, SidebarMenuButton);

        const nestedSub = getPartElement<ISidebarMenuSubProps>(nestedItems, SidebarMenuSub);

        return !!nestedButton?.props.isActive || (!!nestedSub && getHasActiveSub(nestedSub.props.children));
    });
};

const renderRailEntries = (children: ReactNode): ReactNode => {
    const entries = getSubEntries(children);

    const activeIndex = entries.findIndex((entry) => isPartElement<ISidebarMenuSubButtonProps>(entry, SidebarMenuSubButton) && !!entry.props.isActive);

    return <DropdownMenu.RadioGroup
        value={activeIndex === -1 ? "" : `sidebar-rail-entry-${activeIndex}`}
    >
        {
            entries.map((entry, entryIndex) => {
                const key = `sidebar-rail-entry-${entryIndex}`;

                if(isPartElement<ISidebarMenuSubButtonProps>(entry, SidebarMenuSubButton)) {
                    return <DropdownMenu.RadioItem
                        onPress={() => pressSidebarButton({
                            onPress: entry.props.onPress,
                            href: entry.props.href
                        })}
                        isDisabled={entry.props.isDisabled}
                        testID={entry.props.testID}
                        title={entry.props.title}
                        value={key}
                        key={key}
                    />;
                }

                if(!isPartElement<ISidebarMenuItemProps>(entry, SidebarMenuItem)) {
                    return null;
                }

                const nestedItems = Children.toArray(entry.props.children);

                const nestedButton = getPartElement<ISidebarMenuButtonProps>(nestedItems, SidebarMenuButton);

                const nestedSub = getPartElement<ISidebarMenuSubProps>(nestedItems, SidebarMenuSub);

                if(!nestedButton) {
                    return null;
                }

                if(nestedSub) {
                    return <DropdownMenu.Sub
                        key={key}
                    >
                        <DropdownMenu.SubTrigger
                            isDisabled={nestedButton.props.isDisabled}
                            testID={nestedButton.props.testID}
                            title={nestedButton.props.title}
                            icon={nestedButton.props.icon}
                        />
                        <DropdownMenu.SubContent>
                            {renderRailEntries(nestedSub.props.children)}
                        </DropdownMenu.SubContent>
                    </DropdownMenu.Sub>;
                }

                return <DropdownMenu.Item
                    onPress={() => pressSidebarButton({
                        onPress: nestedButton.props.onPress ?? undefined,
                        href: nestedButton.props.href
                    })}
                    isDisabled={nestedButton.props.isDisabled}
                    testID={nestedButton.props.testID}
                    title={nestedButton.props.title}
                    icon={nestedButton.props.icon}
                    key={key}
                />;
            })
        }
    </DropdownMenu.RadioGroup>;
};

const SidebarMenuItem: FC<ISidebarMenuItemProps> = ({
    customTheme: customThemeProp,
    isExpanded: isExpandedProp,
    isCollapsible = true,
    isDefaultExpanded,
    onExpandedChange,
    children,
    style,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.MenuItem"
    });

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction
    } = NCoreUIKitLocalize.useContext(root?.customLocalize);

    const isNested = useContext(SidebarSubContext);

    const pointerType = usePointerType();

    const subId = useId();

    const items = Children.toArray(children);

    const buttonElement = getPartElement<ISidebarMenuButtonProps>(items, SidebarMenuButton);

    const badgeElement = getPartElement<ISidebarMenuBadgeProps>(items, SidebarMenuBadge);

    const subElement = getPartElement<ISidebarMenuSubProps>(items, SidebarMenuSub);

    const hasActiveSub = !!subElement && getHasActiveSub(subElement.props.children);

    const [
        isExpandedState,
        setIsExpandedState
    ] = useState(isDefaultExpanded ?? hasActiveSub);

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        isActionOpen,
        setIsActionOpen
    ] = useState(false);

    const [
        hasAction,
        setHasAction
    ] = useState(false);

    const isExpanded = isExpandedProp ?? isExpandedState;

    const handleExpandedChange = useCallback((nextIsExpanded: boolean) => {
        setIsExpandedState(nextIsExpanded);

        onExpandedChange?.(nextIsExpanded);
    }, [
        onExpandedChange
    ]);

    const size = buttonElement?.props.size ?? "medium";

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const rowMeasures = getSidebarRowMeasures({
        square: measures.square,
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes,
        size
    });

    const placement = getSidebarActionPlacement({
        height: rowMeasures.height,
        inset: rowMeasures.inset,
        touchTargets,
        pointerType,
        iconSizes,
        spaces
    });

    const {
        container: containerDynamicStyle,
        overlay: overlayDynamicStyle
    } = useStyles({
        endOffset: placement.endOffset,
        height: rowMeasures.height,
        isNested,
        measures,
        spaces
    });

    const isActionVisible = isHovered || isFocused || isActionOpen;

    const isActive = buttonElement?.props.isActive ?? false;

    const badgeText = badgeElement ? getSidebarBadgeText({
        max: badgeElement.props.max ?? 99,
        count: badgeElement.props.count,
        locale: activeLocale
    }) : undefined;

    const hasSub = !!subElement;

    const context = useMemo<SidebarItemContextType>(() => ({
        badgeLabel: badgeElement?.props.label,
        handleExpandedChange,
        badge: badgeElement,
        isActionVisible,
        setIsActionOpen,
        isCollapsible,
        hasActiveSub,
        setHasAction,
        setIsFocused,
        isExpanded,
        badgeText,
        hasAction,
        isActive,
        hasSub,
        subId,
        size
    }), [
        handleExpandedChange,
        isActionVisible,
        setIsActionOpen,
        isCollapsible,
        badgeElement,
        hasActiveSub,
        setHasAction,
        setIsFocused,
        isExpanded,
        hasAction,
        badgeText,
        isActive,
        hasSub,
        subId,
        size
    ]);

    const renderRailMenu = () => {
        if(!buttonElement || !subElement) {
            return null;
        }

        return <DropdownMenu
            customLocalize={root?.customLocalize}
            title={buttonElement.props.title}
            side={getSidebarOverlaySide({
                side: root?.side ?? "left",
                direction
            })}
            customTheme={customTheme}
            align="start"
        >
            <DropdownMenu.Trigger>
                <SidebarMenuButton
                    {...buttonElement.props}
                />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                <DropdownMenu.Label
                    title={buttonElement.props.title}
                />
                {renderRailEntries(subElement.props.children)}
            </DropdownMenu.Content>
        </DropdownMenu>;
    };

    const renderBody = () => {
        if(root?.isCollapsed && subElement) {
            return renderRailMenu();
        }

        const others = items.filter((item) => item !== buttonElement && item !== subElement && item !== badgeElement);

        return <>
            {buttonElement}
            {
                others.length > 0 && <View
                    style={[
                        stylesheet.overlay,
                        overlayDynamicStyle
                    ]}
                >
                    {others}
                </View>
            }
            {subElement}
        </>;
    };

    return <SidebarItemContext.Provider
        value={context}
    >
        <View
            {...props}
            onPointerLeave={() => setIsHovered(false)}
            onPointerEnter={() => setIsHovered(true)}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role="listitem"
        >
            {renderBody()}
        </View>
    </SidebarItemContext.Provider>;
};
export default SidebarMenuItem;
